import React from 'react'
import Link from 'next/link'
import {
  useAddress,
  useConnectionStatus,
  useContract,
  useDirectListings,
  useNFT,
  useOwnedNFTs
} from '@thirdweb-dev/react'
import { useTranslation } from 'next-i18next'
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { marketContractAddress } from '~/constants/market'
import { useVault } from '~/hooks/useVault'
import { TrophyDetailComponentDto } from '~/modules/Trophies/Infrastructure/TrophyDetailComponentDto'
import { TrophyMemoryCardList } from '~/modules/TrophyMemories/Infrastructure/Components/TrophyMemoryCardList'
import { TrophyMemoriesEmptyState } from '~/modules/TrophyMemories/Infrastructure/Components/TrophyMemoriesEmptyState'
import { TrophyPrivateMemoryCardList } from './TrophyPrivateMemoryCardList'
import { TrophyMemoriesLoading } from './TrophyMemoriesLoading'
import styles from './TrophyMemories.module.scss'

interface Props {
  trophy: TrophyDetailComponentDto
  isOwner: boolean
}

export const TrophyMemories: React.FC<Props> = ({ trophy, isOwner }) => {
  const { t } = useTranslation('trophy_memories')

  const address = useAddress()
  const connectionStatus = useConnectionStatus()

  const { contract: marketContract } = useContract(marketContractAddress, 'marketplace-v3')
  const { data: listings, isLoading: isListingLoading, error: listingError } = useDirectListings(
    marketContract,
    {
      tokenContract: trophy.collection?.contractAddress,
      tokenId: trophy.tokenId ?? -1,
    }
  )

  const { contract: nftContract } = useContract(trophy.collection?.contractAddress, 'nft-collection')
  const { data: nft, isLoading: isNftLoading, error: nftError } = useNFT(nftContract, trophy.tokenId)

  const {
    data: vault,
    isLoading: isVaultLoading,
    error: vaultError,
  } = useVault(trophy.collection?.contractAddress, trophy.tokenId)
  const { contract: vaultContract } = useContract(vault?.vaultAddress, 'nft-collection')
  const {
    data: ownedFragments,
    isLoading: isOwnedFragmentsLoading,
    error: ownedFragmentsError,
    isError: isOwnedFragmentsError,
  } = useOwnedNFTs(vaultContract, address)

  const hasMemories = trophy.memories.length > 0
  const isLoading =
    isListingLoading ||
    isNftLoading ||
    isVaultLoading ||
    (vault?.vaultAddress !== undefined && isOwnedFragmentsLoading && connectionStatus === 'connected')
  const isFullItemListed = listings ? listings.length > 0 : false
  const isFullItemOwner = connectionStatus === 'connected' && nft?.owner === address
  const isFragmented = vault !== undefined
  const isFragmentOwner = !isOwnedFragmentsLoading && !isOwnedFragmentsError && ownedFragments.length > 0
  const isListed = isFullItemListed || isFragmented
  const isNftOwner = isFullItemOwner || isFragmentOwner

  let memoryShownComponent = null

  if (!hasMemories) {
    memoryShownComponent = <TrophyMemoriesEmptyState trophy={ trophy } />
  } else if (isOwner) {
    memoryShownComponent = <TrophyMemoryCardList memories={ trophy.memories } />
  } else if (isLoading) {
    memoryShownComponent = <TrophyMemoriesLoading />
  } else if (!isListed || (isListed && isNftOwner)) {
    memoryShownComponent = <TrophyMemoryCardList memories={ trophy.memories } />
  } else if (trophy.collection && trophy.tokenId) {
    memoryShownComponent = (
      <TrophyPrivateMemoryCardList
        seriesId={ trophy.collection.seriesId }
        collectionAddress={ trophy.collection.contractAddress }
        tokenId={ trophy.tokenId }
        numMemories={ trophy.memories.length }
      />
    )
  }

  if (listingError) console.error(listingError)
  if (nftError) console.error(nftError)
  if (vaultError) console.error(vaultError)
  if (ownedFragmentsError) console.error(ownedFragmentsError)

  return (
    <div className={ styles.trophyMemories__container }>
      <div className={ styles.trophyMemories__headerContainer }>
        <h1 className={ styles.trophyMemories__highlightedTitle }>
          { t('header_title') }
          <small className={ styles.trophyMemories__subtitle }>
            { t('header_title_added_count', { addedCount: trophy.memories.length }) }
          </small>
        </h1>

        <Link
          href={ `/trophies/${trophy.id}/memories` }
          className={ `
            ${styles.trophyMemories__editTitle}
            ${isOwner && hasMemories ? '' : styles.trophyMemories__editTitle_hidden}
          ` }
          passHref
        >
          { t('header_edit_button') }
          <FontAwesomeIcon
            className={ styles.trophyMemories__editIcon }
            icon={ faPen }
          />
        </Link>
      </div>

      { memoryShownComponent }
    </div>
  )
}
